import React, { useCallback, useEffect, useRef, useState } from "react";
import ModalShell from "./modals/ModalShell";

// App-wide branded dialogs that replace window.alert / confirm / prompt.
//   notify(message, { title })               fire-and-forget message
//   confirmDialog({ title, body, ... })      -> Promise<boolean>
//   typedConfirm({ title, body, phrase })    -> Promise<boolean> (user must type the phrase)
// Call them from anywhere; <DialogProvider> (mounted once) renders them. Without a provider
// (e.g. in tests) they fall back to the browser's own popups.

let bridge = null;

export function notify(message, options = {}) {
  if (bridge) return bridge.notify(message, options);
  window.alert(message);
  return undefined;
}

export function confirmDialog(options) {
  if (bridge) return bridge.confirm(options);
  return Promise.resolve(window.confirm(`${options.title}\n\n${options.body || ""}`));
}

export function typedConfirm(options) {
  if (bridge) return bridge.typed(options);
  return Promise.resolve(window.prompt(`${options.title}\n\n${options.body || ""}\nType ${options.phrase}`) === options.phrase);
}

const LEADING_EMOJI = /^(✅|⚠️|❌|🎉|🚨|ℹ️)\s*/;

function describe(message, explicitTitle) {
  const text = String(message ?? "");
  let tone = "info";
  if (/^(✅|🎉)/.test(text)) tone = "success";
  else if (/^(⚠️|❌|🚨)/.test(text) || /^(error|failed|could not|couldn't|cannot|invalid)/i.test(text)) tone = "error";
  const defaults = { success: "Done", error: "Something went wrong", info: "Heads up" };
  return { tone, title: explicitTitle || defaults[tone], body: text.replace(LEADING_EMOJI, "") };
}

export function DialogProvider({ children }) {
  const [current, setCurrent] = useState(null);
  const queue = useRef([]);
  const showing = useRef(false);
  const [typed, setTyped] = useState("");

  const next = useCallback(() => {
    const item = queue.current.shift();
    showing.current = !!item;
    setTyped("");
    setCurrent(item || null);
  }, []);

  const enqueue = useCallback(
    (item) => {
      queue.current.push(item);
      if (!showing.current) next();
    },
    [next]
  );

  useEffect(() => {
    bridge = {
      notify: (message, options) => enqueue({ kind: "notice", ...describe(message, options.title) }),
      confirm: (options) => new Promise((resolve) => enqueue({ kind: "confirm", ...options, resolve })),
      typed: (options) => new Promise((resolve) => enqueue({ kind: "typed", ...options, resolve })),
    };
    return () => {
      bridge = null;
    };
  }, [enqueue]);

  const close = useCallback(
    (result) => {
      if (current && current.resolve) current.resolve(result);
      next();
    },
    [current, next]
  );

  return (
    <>
      {children}
      {current && (
        <ModalShell
          key={`${current.kind}:${current.title}:${current.body}`}
          title={current.title}
          onClose={() => close(false)}
          footer={
            current.kind === "notice" ? (
              <button type="button" data-autofocus className="rg-modal-btn rg-modal-btn--primary" onClick={() => close(true)}>
                OK
              </button>
            ) : (
              <>
                <button type="button" className="rg-modal-btn rg-modal-btn--ghost" onClick={() => close(false)}>
                  {current.cancelLabel || "Cancel"}
                </button>
                <button
                  type="button"
                  data-autofocus={current.kind === "typed" ? undefined : ""}
                  className={`rg-modal-btn ${current.danger ? "rg-modal-btn--danger" : "rg-modal-btn--primary"}`}
                  disabled={current.kind === "typed" && typed !== current.phrase}
                  onClick={() => close(true)}
                >
                  {current.confirmLabel || "Confirm"}
                </button>
              </>
            )
          }
        >
          {current.body && <p style={{ whiteSpace: "pre-line" }}>{current.body}</p>}
          {current.kind === "typed" && (
            <input
              className="rg-modal-field"
              data-autofocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={`Type ${current.phrase}`}
              aria-label={`Type ${current.phrase} to confirm`}
            />
          )}
        </ModalShell>
      )}
    </>
  );
}
